/* eslint complexity: ["error", 45] */
import React, { PureComponent, memo } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import _ from 'lodash';
import { createStructuredSelector } from 'reselect';
import { RatingLabel, Button, Poster, Loader, Link } from 'components';
import {
  DtoOrTvodButtons,
  SvodAndDtoOrTvodButtons,
  SvodButtons,
} from 'containers/VideoInfo/VideoInfoButtons';
import VideoInfoHeader from 'containers/VideoInfo/VideoInfoHeader';
import apiActions from 'actions/api';
import {
  buyingTariffsInfo,
  deliveryInfo as deliveryInfoSelector,
  genreInfo,
  imdbRatingInfo,
  isAvailableInfo,
  isDTOBoughtInfo,
  isTVODBoughtInfo,
  megogoRatingInfo,
  minDTOPriceInfo,
  minPriceInfo,
  minTVODPriceInfo,
  movieImageInfo,
  movieInfo,
  movieErrorInfo,
  movieSeriesInfo,
  movieTrailerIdInfo,
  restrictionsCodeInfo,
  seasonsCountInfo,
  seriesCountInfo,
  showGotoMegogoSelector,
} from 'selectors/movie';
import { userSelector, getUserHistory as getUserHistorySelector } from 'selectors/user';
import { items as seasonItemsSelector } from 'selectors/season';
import { translate as translateSelector } from 'selectors/translate';
import {
  expirationDateInfo,
  isSVODBoughtInfo,
  subscriptionInfo as subscriptionInfoSelector,
} from 'selectors/subscription';
import videoCollectionSelector from 'selectors/videoCollection';
import {
  geoSelector,
  isEmbedPaySelector,
  getWatermarkSrc,
  isWatermarkClickableEnabled,
} from 'selectors/configuration';
import { idSelector } from 'selectors/app';
import { getStreamRestrictionsCode } from 'selectors/player';
import { PATH } from 'types/router';
import routerActions from 'actions/router';
import { getLastWatched, getPositions } from 'utils/serialObjectProgress';
import generateRulesLink from 'utils/generateRulesLink';
import ellipsify from 'utils/ellipsify';
import playSprite from 'assets/icons/play.svg';
import seriesSmallSprite from 'assets/icons/series_small.svg';
import loginSprite from 'assets/icons/login.svg';
import logoutSprite from 'assets/icons/logout.svg';
import secondTrailerSprite from 'assets/icons/second_trailer.svg';
import logoSprite from 'assets/icons/megogo_logo.svg';
import { purchaseLinkSelector } from 'selectors/configuration';
import Description from './description/description';

import {
  VideoInfoStyled,
  BottomControl,
  ButtonWrapper,
  LinkToSeasons,
  LinkWrap,
  PosterWrap,
  Rating,
  SecondTrailerButton,
  SecondTrailerSeparator,
  TopWrap,
  VideoInfoWrapper,
  RatingLabelWrap,
  LoginButtonWrap,
} from './styles/VideoInfoStyled';

const MemoizedDescription = memo(Description);

@connect(
  createStructuredSelector({
    movie: movieInfo,
    genre: genreInfo,
    errorInfo: movieErrorInfo,
    minPrice: minPriceInfo,
    minDTOPrice: minDTOPriceInfo,
    minTVODPrice: minTVODPriceInfo,
    seasonsCount: seasonsCountInfo,
    seriesCount: seriesCountInfo,
    restrictionsCode: restrictionsCodeInfo,
    streamRestrictionsCode: getStreamRestrictionsCode,
    user: userSelector,
    userHistory: getUserHistorySelector,
    translate: translateSelector,
    seasonItems: seasonItemsSelector,
    subscriptionInfo: subscriptionInfoSelector,
    videoCollection: videoCollectionSelector,
    deliveryInfo: deliveryInfoSelector,
    imdbRating: imdbRatingInfo,
    megogoRating: megogoRatingInfo,
    movieImage: movieImageInfo,
    movieSeries: movieSeriesInfo,
    movieTrailerId: movieTrailerIdInfo,
    isAvailable: isAvailableInfo,
    isDTOBought: isDTOBoughtInfo,
    isTVODBought: isTVODBoughtInfo,
    isSVODBought: isSVODBoughtInfo,
    expirationDate: expirationDateInfo,
    geo: geoSelector,
    id: idSelector,
    buyingTariffs: buyingTariffsInfo,
    isEmbedPay: isEmbedPaySelector,
    showGotoMegogo: showGotoMegogoSelector,
    watermarkSrc: getWatermarkSrc,
    isWatermarkClickable: isWatermarkClickableEnabled,
    purchaseLink: purchaseLinkSelector,
  }),
  {
    ...apiActions,
    ...routerActions,
  }
)
class VideoInfo extends PureComponent {
  static propTypes = {
    // actions
    translate: PropTypes.func.isRequired,
    postLogout: PropTypes.func.isRequired,
    goTo: PropTypes.func.isRequired,
    // props
    movie: PropTypes.object,
    user: PropTypes.object,
    genre: PropTypes.string,
    seasonsCount: PropTypes.number,
    seriesCount: PropTypes.number,
    restrictionsCode: PropTypes.string,
    streamRestrictionsCode: PropTypes.string,
    currentLocation: PropTypes.object,
    subscriptionInfo: PropTypes.object,
    videoCollection: PropTypes.object,
    minPrice: PropTypes.string,
    deliveryInfo: PropTypes.object,
    imdbRating: PropTypes.string,
    megogoRating: PropTypes.string,
    movieImage: PropTypes.object,
    movieSeries: PropTypes.bool,
    movieTrailerId: PropTypes.number,
    isAvailable: PropTypes.bool,
    isDTOBought: PropTypes.bool,
    isTVODBought: PropTypes.bool,
    isSVODBought: PropTypes.bool,
    minDTOPrice: PropTypes.string,
    minTVODPrice: PropTypes.string,
    geo: PropTypes.string,
    id: PropTypes.number,
    expirationDate: PropTypes.string,
    buyingTariffs: PropTypes.object,
    isEmbedPay: PropTypes.bool,
    isMobile: PropTypes.bool,
    errorInfo: PropTypes.object,
    userHistory: PropTypes.object,
    customRestrictionMessage: PropTypes.string,
    showGotoMegogo: PropTypes.bool,
    watermarkSrc: PropTypes.string,
    isWatermarkClickable: PropTypes.bool,
    purchaseLink: PropTypes.string,
  };

  static defaultProps = {
    movie: undefined,
    user: {},
    genre: '',
    seasonsCount: null,
    seriesCount: null,
    restrictionsCode: null,
    streamRestrictionsCode: null,
    currentLocation: {},
    videoCollection: {},
    minPrice: null,
    deliveryInfo: {},
    subscriptionInfo: {},
    imdbRating: null,
    megogoRating: null,
    movieImage: {},
    movieSeries: null,
    movieTrailerId: null,
    isAvailable: false,
    isDTOBought: false,
    isTVODBought: false,
    isSVODBought: false,
    minDTOPrice: '',
    minTVODPrice: '',
    geo: '',
    id: 0,
    expirationDate: null,
    buyingTariffs: {},
    isEmbedPay: true,
    isMobile: false,
    errorInfo: {},
    userHistory: {},
    customRestrictionMessage: '',
    showGotoMegogo: false,
    watermarkSrc: '',
    isWatermarkClickable: false,
  };

  constructor(props) {
    super(props);
  }

  componentDidMount() {
    const initEllipsify = setInterval(() => {
      if (this.titleRef) {
        ellipsify(this.titleRef);
        clearInterval(initEllipsify);
      }
    }, 150);
  }

  onLoginButtonClick = () => {
    const { user, postLogout, goTo } = this.props;
    if (user.isLogin) {
      postLogout();
    } else {
      goTo(PATH.ENTER);
    }
  };

  onWatchButtonClick = () => {
    const {
      id,
      goTo,
      user: { isLogin },
      userHistory,
    } = this.props;
    let lastWatched;
    if (isLogin) {
      lastWatched = _.get(userHistory, 'lastWatched.episode_id');
      if (lastWatched) {
        // don't use 'start' parameter, because we get 'play_start_time' parameter in GET/stream
        goTo(PATH.PLAYER, { video_id: lastWatched });
      } else {
        goTo(PATH.PLAYER);
      }
    } else {
      lastWatched = getLastWatched(id);
      if (lastWatched) {
        goTo(PATH.PLAYER, {
          video_id: lastWatched,
          start: _.get(getPositions(), `${lastWatched}.1`),
        });
      } else {
        goTo(PATH.PLAYER, { start: _.get(getPositions(), `${id}.1`) });
      }
    }
  };

  // prevent routing to accordion seasons, if an object has embeding restrictions
  checkEpisodesAvailable = () => {
    const { restrictionsCode, goTo } = this.props;
    const hideSeasons = restrictionsCode === 'BIZ_CLASS' || restrictionsCode === 'CONTRACT';

    if (!hideSeasons) {
      goTo(PATH.EPISODES);
    }
  };

  playTrailer = () => this.props.goTo(PATH.PLAYER, { video_id: this.props.movieTrailerId });

  assignTitleRef = (ref) => {
    this.titleRef = ref;
  };

  get utmSource() {
    try {
      const [ancestor] = location.ancestorOrigins;

      return ancestor.replace(/^.+:\/\//, '');
    } catch (e) {
      return false;
    }
  }

  get utmLabels() {
    const fragments = [
      ['utm_medium', 'vclicks'],
      ['utm_campaign', 'embed'],
      ['utm_source', this.utmSource],
    ].filter(([name, value]) => name && value);

    return fragments.map(([name, value]) => `${name}=${value}`).join('&');
  }

  renderButtons = () => {
    let rendered;
    const {
      deliveryInfo: { hasADVOD, hasTVOD, hasSVOD, hasDTO, hasFVOD },
      subscriptionInfo,
      isAvailable,
      goTo,
      translate,
      minDTOPrice,
      minTVODPrice,
      minPrice,
      geo,
      restrictionsCode,
      movie,
      buyingTariffs,
      isEmbedPay,
      isMobile,
      purchaseLink,
    } = this.props;

    const isTnB = _.get(subscriptionInfo, 'tariffs.0.isTryAndBuy', false);
    const mustPayOnSite = !isEmbedPay && !hasADVOD && !hasFVOD && !isAvailable;
    const purchaseGoTo =
      !isAvailable && purchaseLink
        ? () => {
            window.open(purchaseLink, '_blank');
          }
        : goTo;

    if (mustPayOnSite || restrictionsCode === 'BIZ_CLASS' || restrictionsCode === 'CONTRACT') {
      rendered = (
        <Button
          buttonStyle="background"
          tag="a"
          href={generateRulesLink(`view/${movie.id}?${this.utmLabels}`)}
          textSize="xs"
          size="l"
          align="center"
          textColor="white"
          target="_blank"
          text={translate('watch_on_megogo')}
          icon={playSprite.id}
          isMobile={isMobile}
          iconSize={15}
        />
      );
    } else if (hasADVOD || hasFVOD) {
      rendered = (
        <Button
          buttonStyle="background"
          textSize="xs"
          size="l"
          align="center"
          textColor="white"
          text={translate('watch')}
          icon={playSprite.id}
          iconSize={15}
          isMobile={isMobile}
          onClick={this.onWatchButtonClick}
        />
      );
    } else if (hasSVOD && (hasTVOD || hasDTO)) {
      rendered = (
        <SvodAndDtoOrTvodButtons
          hasDTO={hasDTO}
          hasTVOD={hasTVOD}
          isAvailable={isAvailable}
          translate={translate}
          goTo={purchaseGoTo}
          subscriptionInfo={subscriptionInfo}
          isTnB={isTnB}
          minPrice={minPrice}
          geo={geo}
          buyingTariffs={buyingTariffs}
          isMobile={isMobile}
        />
      );
    } else if (hasSVOD) {
      rendered = (
        <SvodButtons
          isAvailable={isAvailable}
          translate={translate}
          goTo={purchaseGoTo}
          subscriptionInfo={subscriptionInfo}
          isTnB={isTnB}
          geo={geo}
          isMobile={isMobile}
        />
      );
    } else if (hasDTO || hasTVOD) {
      rendered = (
        <DtoOrTvodButtons
          hasDTO={hasDTO}
          hasTVOD={hasTVOD}
          isAvailable={isAvailable}
          translate={translate}
          goTo={purchaseGoTo}
          minTVODPrice={minTVODPrice}
          minDTOPrice={minDTOPrice}
          buyingTariffs={buyingTariffs}
          isMobile={isMobile}
        />
      );
    }
    return rendered;
  };

  render() {
    const {
      movie,
      translate,
      goTo,
      user,
      genre,
      seasonsCount,
      seriesCount,
      imdbRating,
      megogoRating,
      movieImage,
      movieSeries,
      movieTrailerId,
      minPrice,
      isDTOBought,
      isTVODBought,
      isSVODBought,
      expirationDate,
      isAvailable,
      buyingTariffs,
      deliveryInfo: { hasTVOD, hasSVOD, hasDTO },
      videoCollection: { videoCollectionPosters: posters },
      subscriptionInfo,
      isMobile,
      watermarkSrc,
      isWatermarkClickable,
      restrictionsCode,
      isEmbedPay,
      hasADVOD,
      hasFVOD,
    } = this.props;

    let buyFrom = '';
    let purchasedIcon = false;

    const bySubscription = hasSVOD ? translate('subscription') : '';

    if (hasDTO || hasTVOD) {
      if (!isDTOBought && !isTVODBought && minPrice) {
        let text = translate('from');
        if (hasTVOD && !hasDTO) {
          text = _.get(buyingTariffs, 'tvod', []).length > 1 ? translate('from') : translate('for');
        } else if (!hasTVOD && hasDTO) {
          text = _.get(buyingTariffs, 'dto', []).length > 1 ? translate('from') : translate('for');
        }
        buyFrom = `${text} ${minPrice}`;
      } else if (isDTOBought || isTVODBought) {
        buyFrom = translate('purchased');
      }
    }

    if (isSVODBought || isDTOBought || isTVODBought) {
      purchasedIcon = true;
    }

    if (_.get(movie, 'isLoading')) {
      return <Loader />;
    }

    const disableTrailer =
      (!isEmbedPay && !hasADVOD && !hasFVOD && !isAvailable) ||
      restrictionsCode === 'BIZ_CLASS' ||
      restrictionsCode === 'CONTRACT';
    const title = _.get(movie, 'title');
    const year = _.get(movie, 'year');

    return (
      <VideoInfoStyled>
        <PosterWrap>
          <Poster
            mainPoster={movieImage.big}
            trailer={!disableTrailer && movieTrailerId > 0}
            onPlayTrailer={this.playTrailer}
            subscription={!!subscriptionInfo.description}
            posters={posters}
            translate={translate}
          />
        </PosterWrap>
        <VideoInfoWrapper>
          <TopWrap>
            {movie &&
              title && (
                <VideoInfoHeader
                  title={title}
                  genre={genre}
                  year={year}
                  isSVODBought={isSVODBought}
                  isTVODBought={isTVODBought}
                  isDTOBought={isDTOBought}
                  expirationDate={expirationDate}
                  bySubscription={bySubscription}
                  buyFrom={buyFrom}
                  purchasedIcon={purchasedIcon}
                  translate={translate}
                  assignTitleRef={this.assignTitleRef}
                />
              )}
            {movieSeries &&
              !movie.single_episode &&
              Boolean(seasonsCount) && (
                <LinkToSeasons>
                  <Button
                    icon={seriesSmallSprite.id}
                    textColor="gray"
                    hoverColor="blue_navyblue"
                    textSize="fs_xxs"
                    align="center"
                    iconSize={17}
                    separation={3}
                    isMobile={isMobile}
                    disable={!isAvailable}
                    text={`${seasonsCount} ${translate('count_seasons', {
                      seasons: seasonsCount,
                    })},
                       ${seriesCount} ${translate('count_episodes', {
                      episodes: seriesCount,
                    })}`}
                    onClick={isAvailable ? this.checkEpisodesAvailable : null}
                  />
                </LinkToSeasons>
              )}
          </TopWrap>
          <MemoizedDescription
            {...{
              isAvailable,
              hasTVOD, // Transactional Video on Demand,
              hasSVOD, // Subscription...,
              hasDTO, // Download To Own,
              subscriptionInfo,
              translate,
              movie,
            }}
          />
          {(imdbRating || megogoRating) && (
            <Rating>
              {imdbRating > 0 && (
                <RatingLabelWrap>
                  <RatingLabel name="IMDb" value={imdbRating} />
                </RatingLabelWrap>
              )}
              {megogoRating > 0 && (
                <RatingLabelWrap>
                  <RatingLabel name="MGG" value={megogoRating} />
                </RatingLabelWrap>
              )}
              {!disableTrailer &&
                movieTrailerId > 0 &&
                (megogoRating > 0 || imdbRating > 0) && <SecondTrailerSeparator />}
              {!disableTrailer &&
                movieTrailerId > 0 && (
                  <SecondTrailerButton>
                    <Button
                      icon={secondTrailerSprite.id}
                      buttonStyle="transparent"
                      hoverColor="blue_navyblue"
                      textColor="blue_doger"
                      textType="uppercase"
                      textFont="din_bold"
                      textSize="fs_xs"
                      iconSize={28}
                      isMobile={isMobile}
                      text={translate('trailer')}
                      separation={10}
                      onClick={() => goTo(PATH.PLAYER, { video_id: movieTrailerId })}
                    />
                  </SecondTrailerButton>
                )}
            </Rating>
          )}
          <ButtonWrapper>{this.renderButtons()}</ButtonWrapper>
          <BottomControl>
            <LoginButtonWrap data-role="login-wrapper">
              <Button
                text={user.isLogin ? translate('logout') : translate('enter')}
                icon={user.isLogin ? logoutSprite.id : loginSprite.id}
                buttonStyle="transparent"
                textType="uppercase"
                textColor="gray"
                textFont="din_bold"
                hoverColor="blue_navyblue"
                iconSize={18}
                align="center"
                isMobile={isMobile}
                separation={5}
                onClick={this.onLoginButtonClick}
              />
            </LoginButtonWrap>
            {movie &&
              watermarkSrc && (
                <LinkWrap clickable={isWatermarkClickable}>
                  <Link
                    href={generateRulesLink(`view/${movie.id}`)}
                    title=""
                    type="logo"
                    iconColor="#333"
                    iconName={logoSprite.id}
                    iconHeight={12}
                    iconWidth={80}
                  />
                </LinkWrap>
              )}
          </BottomControl>
        </VideoInfoWrapper>
      </VideoInfoStyled>
    );
  }
}

export default VideoInfo;
